/**
 * Bero platform console — Create society. POST /api/platform/societies
 * creates the society LIVE and emails the owner an invite link
 * (claim-owner.tsx) to set their password and land as MASTER_ADMIN.
 * SUPER_ADMIN only server-side (@require_platform("SUPER_ADMIN")) and the nav
 * link in platform/_layout.tsx is SUPER_ADMIN-only too; a non-SUPER_ADMIN who
 * still navigates here directly (deep link, stale bookmark) sees the API's
 * own "Forbidden" surface on submit rather than a duplicated client-side gate.
 * Also reachable from Leads' Convert action via
 * /platform/create?leadId=&name=&city= (Task 9 hand-off) — prefills the
 * society fields and passes convertLeadId so the API marks the source lead
 * CONVERTED.
 */
import { useLocalSearchParams } from 'expo-router';
import React from 'react';
import { View } from 'react-native';

import { space } from '@bero/tokens';
import { Icon } from '@/features/account-admin/icons';
import { useT } from '@/i18n';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Button, Chip, Field, Input, Screen, SectionLabel } from '@/ui';

const PLANS = ['trial', 'starter', 'pro'] as const;
const PLAN_LABEL: Record<(typeof PLANS)[number], string> = { trial: 'Trial', starter: 'Starter', pro: 'Pro' };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function slugify(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 70);
}

type CreateResult = { societyId: number; emailSent: boolean; ownerEmail: string };

export default function PlatformCreateScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const params = useLocalSearchParams<{ leadId?: string; name?: string; city?: string }>();

  const [name, setName] = React.useState(params.name ?? '');
  const [city, setCity] = React.useState(params.city ?? '');
  const [address, setAddress] = React.useState('');
  const [slug, setSlug] = React.useState(() => slugify(params.name ?? ''));
  const [slugTouched, setSlugTouched] = React.useState(false);
  const [plan, setPlan] = React.useState<(typeof PLANS)[number]>('trial');
  const [ownerName, setOwnerName] = React.useState('');
  const [ownerEmail, setOwnerEmail] = React.useState('');
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [busy, setBusy] = React.useState(false);
  const [submitErr, setSubmitErr] = React.useState('');
  const [result, setResult] = React.useState<CreateResult | null>(null);
  const [resending, setResending] = React.useState(false);

  React.useEffect(() => {
    if (!slugTouched) void Promise.resolve().then(() => setSlug(slugify(name)));
  }, [name, slugTouched]);

  const validate = () => {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = t('plat_create_err_name');
    if (!city.trim()) e.city = t('plat_create_err_city');
    if (!ownerName.trim()) e.ownerName = t('plat_create_err_owner_name');
    if (!EMAIL_RE.test(ownerEmail.trim())) e.ownerEmail = t('onb_signup_err_email');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (busy || !validate()) return;
    setBusy(true);
    setSubmitErr('');
    try {
      const body = await api.post<{ society: { id: number }; emailSent: boolean }>(
        '/api/platform/societies',
        {
          name: name.trim(),
          city: city.trim(),
          address: address.trim(),
          slug: slug.trim() || undefined,
          plan,
          ownerName: ownerName.trim(),
          ownerEmail: ownerEmail.trim(),
          convertLeadId: params.leadId ? Number(params.leadId) : undefined,
        },
      );
      setResult({ societyId: body.society.id, emailSent: body.emailSent, ownerEmail: ownerEmail.trim() });
    } catch (e) {
      setSubmitErr((e as { error?: string })?.error ?? t('plat_create_err_generic'));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (!result || resending) return;
    setResending(true);
    try {
      const res = await api.post<{ emailSent: boolean }>(`/api/platform/societies/${result.societyId}/resend-invite`);
      setResult((r) => (r ? { ...r, emailSent: res.emailSent } : r));
    } catch {
      // best-effort resend — the panel keeps showing whatever emailSent state it already has
    } finally {
      setResending(false);
    }
  };

  if (result) {
    return (
      <Screen padded fullWidth style={{ paddingTop: space.s5 }}>
        <View style={{ alignItems: 'center', paddingVertical: 64, paddingHorizontal: space.s6 }}>
          <Icon
            name={result.emailSent ? 'mail' : 'alert-triangle'}
            size={40}
            color={result.emailSent ? colors.primary500 : colors.textTertiary}
          />
          <AppText variant="headingSm" style={{ marginTop: space.s4, textAlign: 'center' }}>
            {t('plat_create_done_title')}
          </AppText>
          <AppText variant="bodyMd" tone="secondary" style={{ marginTop: space.s2, textAlign: 'center', maxWidth: 380 }}>
            {result.emailSent ? t('plat_create_done_body', { email: result.ownerEmail }) : t('plat_create_email_failed')}
          </AppText>
          <Button
            title={t('plat_create_resend')}
            kind="secondary"
            loading={resending}
            onPress={() => void resend()}
            style={{ marginTop: space.s5 }}
          />
        </View>
      </Screen>
    );
  }

  return (
    <Screen padded fullWidth style={{ paddingTop: space.s5 }}>
      <View style={{ maxWidth: 560, gap: space.s4 }}>
        <SectionLabel>{t('plat_create_society_sec')}</SectionLabel>
        <Field label={t('plat_create_name')} error={errors.name}>
          <Input value={name} onChangeText={setName} hasError={!!errors.name} editable={!busy} />
        </Field>
        <Field label={t('plat_create_city')} error={errors.city}>
          <Input value={city} onChangeText={setCity} hasError={!!errors.city} editable={!busy} />
        </Field>
        <Field label={t('plat_create_address')}>
          <Input value={address} onChangeText={setAddress} editable={!busy} />
        </Field>
        <Field label={t('plat_create_slug')} help={t('plat_create_slug_help')}>
          <Input
            value={slug}
            onChangeText={(v) => { setSlugTouched(true); setSlug(v); }}
            autoCapitalize="none"
            editable={!busy}
          />
        </Field>
        <Field label={t('plat_create_plan')}>
          <View style={{ flexDirection: 'row', gap: space.s2 }}>
            {PLANS.map((p) => (
              <Chip key={p} label={PLAN_LABEL[p]} active={plan === p} onPress={busy ? undefined : () => setPlan(p)} />
            ))}
          </View>
        </Field>

        <SectionLabel style={{ marginTop: space.s2 }}>{t('plat_create_owner_sec')}</SectionLabel>
        <Field label={t('plat_create_owner_name')} error={errors.ownerName}>
          <Input value={ownerName} onChangeText={setOwnerName} hasError={!!errors.ownerName} editable={!busy} />
        </Field>
        <Field label={t('plat_create_owner_email')} error={errors.ownerEmail}>
          <Input
            value={ownerEmail}
            onChangeText={setOwnerEmail}
            autoCapitalize="none"
            keyboardType="email-address"
            hasError={!!errors.ownerEmail}
            editable={!busy}
          />
        </Field>

        {submitErr ? <AppText variant="bodySm" style={{ color: colors.danger500 }}>{submitErr}</AppText> : null}
        <Button
          title={busy ? t('plat_create_creating') : t('plat_create_submit')}
          loading={busy}
          onPress={() => void submit()}
          style={{ alignSelf: 'flex-start', marginTop: space.s2 }}
        />
      </View>
    </Screen>
  );
}
